import React from 'react';
import { Link } from 'react-router-dom';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';
import { ShieldAlert } from 'lucide-react';

const TermsPage = () => (
  <div className="min-h-screen bg-zinc-950 text-zinc-200" data-testid="terms-page">
    <header className="border-b border-zinc-800 bg-zinc-900/60 backdrop-blur sticky top-0 z-40">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="text-sm text-zinc-400 hover:text-white">← Tempus Dispatch</Link>
        <div className="flex gap-4 text-xs">
          <Link to="/terms" className="text-white font-medium">Terms</Link>
          <Link to="/privacy" className="text-zinc-400 hover:text-white">Privacy</Link>
        </div>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-1">Terms of Service</h1>
        <p className="text-xs text-zinc-500">Effective: February 1, 2026 · Last updated: February 1, 2026</p>
      </div>

      <section className="bg-amber-500/10 border border-amber-500/30 rounded-md p-4 flex gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-amber-200 mb-1">Platform Disclaimer</p>
          <p className="text-amber-100/90 leading-relaxed">
            Tempus Dispatch LLC ("Tempus", "we", "us") operates a Software-as-a-Service (SaaS)
            platform that facilitates communication, document generation, and workflow automation
            between motor carriers, brokers, and shippers. <strong>Tempus is NOT a freight broker,
            freight forwarder, motor carrier, or party to any transportation contract.</strong> Tempus
            does not arrange transportation of property, does not hold an FMCSA Broker Authority
            (MC number issued under 49 CFR Part 371), and does not receive, hold, or disburse freight payments.
          </p>
        </div>
      </section>

      <Section title="1. Acceptance">
        By creating an account or accessing tempusdispatch.com you agree to these Terms.
        If you do not agree, do not use the platform.
      </Section>

      <Section title="2. Nature of the Service">
        <p>Tempus provides the following software functionality:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Load board listing & search</li>
          <li>Bidding / counter-offer workflow between users</li>
          <li>Auto-matching algorithms and AI suggestions (informational only)</li>
          <li>Rate confirmation & invoice document generation (templates only)</li>
          <li>Email / messaging dispatch between users</li>
          <li>Optional "Auto-Confirm on Your Behalf" feature — when enabled by a user, Tempus
              acts solely as the user's <em>software agent</em>, executing their pre-configured
              rules. The user remains the principal in all communications and contracts.</li>
        </ul>
        <p className="mt-2">Tempus does <strong>not</strong>:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Accept or arrange transportation in interstate commerce on its own account</li>
          <li>Take title, custody, or possession of freight</li>
          <li>Process freight payments between brokers and carriers (payments are made directly between the parties)</li>
          <li>Guarantee load availability, rate accuracy, or payment from any third party</li>
        </ul>
      </Section>

      <Section title="3. Payment Flow">
        <p>
          All freight payments flow <strong>directly</strong> between the shipper/broker and the
          motor carrier outside of Tempus. Tempus does not hold customer funds in trust, does not
          maintain an FMCSA Broker Surety Bond (BMC-84/85), and does not act as an escrow agent.
        </p>
        <p className="mt-2">
          The only payments Tempus collects are <strong>software subscription fees</strong> billed
          directly to the subscriber (carrier, dispatcher, or shipper) via Stripe. These fees are
          for use of the software platform only and are unrelated to the value of any freight
          transaction moved through the platform.
        </p>
      </Section>

      <Section title="4. User Responsibilities">
        <ul className="list-disc pl-6 space-y-1">
          <li>Carriers must maintain valid MC/DOT authority, insurance, and equipment in compliance with FMCSA regulations.</li>
          <li>Brokers must maintain their own Broker Authority, surety bond, and FMCSA compliance.</li>
          <li>Users are solely responsible for the accuracy of information they post, including rates, equipment, and pickup/delivery details.</li>
          <li>Users must pay carriers directly for freight moved. Tempus is not liable for non-payment by any user.</li>
        </ul>
      </Section>

      <Section title="5. No Brokerage Liability">
        <p>Tempus is not a freight broker (49 USC § 13904) and assumes no liability for freight contracts entered into via the platform. All freight transactions occur directly between shippers/brokers and motor carriers.</p>
        <p className="mt-2 italic text-zinc-400 text-xs"><strong>Platform Description:</strong> Tempus Dispatch LLC is a technology platform and carrier dispatch agent only. It is not a licensed freight broker. All freight transactions are between shipper/broker and carrier directly.</p>
      </Section>

      <Section title="5a. Carrier Commitment Policy">
        <p>Carriers who accept or are auto-confirmed onto a load are bound to that load as the <strong>principal</strong>. To protect brokers and shippers from no-shows:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Carriers must provide a <strong>minimum of 4 hours' notice</strong> of inability to cover a confirmed load.</li>
          <li>Failure to provide adequate notice results in a Trust Score penalty.</li>
          <li>A 3rd "Cannot Cover" event within 30 days automatically disables Auto-Confirm and triggers a suspension warning. A 4th event may result in account suspension.</li>
        </ul>
      </Section>

      <Section title="5b. Platform Liability Cap & Re-dispatch Guarantee">
        <p>In the event a carrier fails to cover an auto-confirmed load, Tempus Dispatch's <strong>sole obligation</strong> is to source a replacement carrier within 30 minutes from its pre-vetted backup queue. Tempus does not assume financial liability for the underlying freight contract, late fees, lost cargo, or downstream damages of any kind.</p>
        <p className="mt-2"><strong>Re-dispatch Guarantee:</strong> Tempus guarantees re-dispatch to a qualified backup carrier within <strong>30 minutes</strong> of coverage failure on any auto-confirmed load. If all queued backups decline or no qualified backup exists, the load is flagged for manual intervention and the founder is notified immediately.</p>
        <p className="mt-2 text-zinc-400 text-xs">
          Because Tempus does not arrange transportation for compensation, it is not subject to
          49 USC § 13904 (Broker Authority) or the bonding requirements of 49 USC § 13906.
          Any claim arising from a freight transaction (non-payment, cargo loss, damage, delay)
          must be pursued between the broker/shipper and the carrier directly. Tempus is not a
          party to and shall not be joined in such claims.
        </p>
      </Section>

      <Section title="6. AI Outputs & Rate Suggestions">
        Tempus's AI features (rate predictions, lane intelligence, driver matching, autopilot)
        produce informational outputs only. They are not legal, financial, or commercial advice.
        Users are responsible for verifying all outputs before relying on them.
      </Section>

      <Section title="7. Limitation of Liability">
        To the maximum extent permitted by law, Tempus's aggregate liability for any claim is
        limited to the lesser of (a) fees you paid Tempus in the 12 months preceding the claim,
        or (b) $100. Tempus disclaims all consequential, incidental, indirect, and punitive damages.
      </Section>

      <Section title="8. Termination">
        Either party may terminate the subscription at any time. Upon termination, your access
        to the platform ends; outstanding subscription fees remain due.
      </Section>

      <Section title="9. Governing Law">
        These Terms are governed by the laws of the State of Delaware, USA, without regard to
        conflict of laws principles. Any dispute shall be resolved exclusively in the state or
        federal courts located in New Castle County, Delaware.
      </Section>

      <Section title="10. Contact">
        Tempus Dispatch LLC · givi@tempusdispatch.com · www.tempusdispatch.com
      </Section>
    </main>

    <PlatformDisclaimer />
  </div>
);

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="text-lg font-semibold text-white">{title}</h2>
    <div className="text-sm text-zinc-300 leading-relaxed">{children}</div>
  </section>
);

export default TermsPage;
